import {
  convertExampleToCodegenInput,
  convertScenarioToCodegenInput,
  Endpoint,
} from '@square/ignition';
import { SpecificationVersionInfo } from '@squareup/dex-types-oas-path';

type EndpointExampleScenario = Parameters<
  typeof convertScenarioToCodegenInput
>[0] & {
  environment?: ReturnType<typeof convertExampleToCodegenInput>['environment'];
};

type EndpointCodegenInput = ReturnType<typeof convertExampleToCodegenInput>;

type EndpointDocsLocalCodegenInput = EndpointCodegenInput & {
  authorization?: EndpointCodegenInput['authentication'];
};

type EndpointDocsLocalExample =
  | EndpointExampleScenario
  | EndpointDocsLocalCodegenInput;
const SANDBOX_ENVIRONMENT = 'sandbox' as EndpointCodegenInput['environment'];

function parseEndpointTag(raw: string): {
  method: string;
  path: string;
} | null {
  const trimmed = raw.trim();
  const idx = trimmed.indexOf(' ');
  if (idx <= 0) {
    return null;
  }
  return {
    method: trimmed.slice(0, idx).toUpperCase(),
    path: trimmed.slice(idx + 1).trim(),
  };
}

function specOptionsFor(api?: string): SpecificationVersionInfo {
  // "latest" is the implicit default in our OAS hooks (omit version);
  // any other value is treated as a published square_version.
  const isLatest = !api || api === 'latest';
  return {
    grammar: 'open-api-3',
    namespace: 'square',
    version: isLatest ? undefined : api,
    isUnpublished: false,
  };
}

// Pick the codegen input for the requested example. Fern's example names
// don't always exist on our spec; when the name misses, prefer the first
// scenario (deterministic) over the endpoint's default to avoid silently
// swapping a populated body for an empty one.
function resolveCodegenInput(
  endpoint: Endpoint,
  exampleName?: string,
  exampleScenario?: EndpointDocsLocalExample
): EndpointCodegenInput {
  if (exampleScenario) {
    if (isDocsCodegenInput(exampleScenario)) {
      return normalizeDocsCodegenInput(
        toDocsCodegenInput(exampleScenario),
        endpoint,
        exampleScenario.environment,
        false
      );
    }

    const codegenInput = convertScenarioToCodegenInput(
      exampleScenario,
      endpoint
    );
    return normalizeDocsCodegenInput(
      codegenInput,
      endpoint,
      exampleScenario.environment,
      false
    );
  }

  const scenarios = endpoint.exampleScenarios || [];
  const namedScenario =
    exampleName && scenarios.find((s) => s.name === exampleName);
  const scenario = namedScenario || scenarios[0];
  if (scenario) {
    return normalizeDocsCodegenInput(
      convertScenarioToCodegenInput(scenario, endpoint),
      endpoint
    );
  }
  return normalizeDocsCodegenInput(
    convertExampleToCodegenInput(endpoint),
    endpoint
  );
}

function normalizeDocsCodegenInput(
  codegenInput: EndpointCodegenInput,
  endpoint: Endpoint,
  environment?: EndpointCodegenInput['environment'],
  defaultEnvironment = true
): EndpointCodegenInput {
  const authentication = {
    ...codegenInput.authentication,
    accessToken: codegenInput.authentication?.accessToken || '{ACCESS_TOKEN}',
  };

  const normalized: EndpointCodegenInput = {
    ...codegenInput,
    authentication,
  };
  const pathParameters = normalizePathPlaceholders(
    codegenInput.pathParameters,
    endpoint
  );
  const queryParameters = normalizeQueryPlaceholders(
    codegenInput.queryParameters
  );
  if (pathParameters) {
    normalized.pathParameters = pathParameters;
  }
  if (queryParameters) {
    normalized.queryParameters = queryParameters;
  }

  const resolvedEnvironment =
    environment ||
    codegenInput.environment ||
    (defaultEnvironment ? SANDBOX_ENVIRONMENT : undefined);

  return resolvedEnvironment
    ? { ...normalized, environment: resolvedEnvironment }
    : normalized;
}

function normalizePathPlaceholders(
  params: EndpointCodegenInput['pathParameters'],
  endpoint: Endpoint
): EndpointCodegenInput['pathParameters'] {
  const endpointPathParameters = pathParametersForEndpoint(endpoint);
  if (endpointPathParameters.length === 0 || !params) {
    return params;
  }

  let normalizedParams: Record<string, unknown> = {};
  if (typeof params === 'string' && endpointPathParameters.length === 1) {
    const pathParameter = endpointPathParameters[0];
    if (pathParameter) {
      normalizedParams = { [pathParameter]: params };
    }
  } else if (params && typeof params === 'object' && !Array.isArray(params)) {
    normalizedParams = { ...params };
  }

  const providedParams: Record<string, unknown> = Object.fromEntries(
    endpointPathParameters.flatMap((key) =>
      normalizedParams[key] !== undefined
        ? [[key, wrapLegacyPlaceholder(normalizedParams[key])]]
        : []
    )
  );
  if (Object.keys(providedParams).length === 0) {
    return params;
  }
  return providedParams as EndpointCodegenInput['pathParameters'];
}

function normalizeQueryPlaceholders(
  params: EndpointCodegenInput['queryParameters']
): EndpointCodegenInput['queryParameters'] {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return params;
  }
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      key === 'cursor' || key.endsWith('_id')
        ? wrapLegacyPlaceholder(value)
        : value,
    ])
  ) as EndpointCodegenInput['queryParameters'];
}

function wrapLegacyPlaceholder(value: unknown) {
  if (
    typeof value === 'string' &&
    /^[A-Z_]+$/.test(value) &&
    !value.startsWith('{{')
  ) {
    return `{{${value}}}`;
  }
  return value;
}

function pathParametersForEndpoint(endpoint: Endpoint) {
  return [...endpoint.path.matchAll(/{([^}]+)}/g)].flatMap((match) =>
    match[1] ? [match[1]] : []
  );
}

function isDocsCodegenInput(
  example: EndpointDocsLocalExample
): example is EndpointDocsLocalCodegenInput {
  return [
    'authentication',
    'authorization',
    'bodyParameters',
    'pathParameters',
    'queryParameters',
    'fileParameters',
    'environment',
  ].some((key) => key in example);
}

function toDocsCodegenInput(
  example: EndpointDocsLocalCodegenInput
): EndpointCodegenInput {
  const { authorization, ...codegenInput } = example;
  if (!codegenInput.authentication && authorization) {
    return {
      ...codegenInput,
      authentication: authorization,
    };
  }
  return codegenInput;
}

export { parseEndpointTag, specOptionsFor, resolveCodegenInput };
export type { EndpointDocsLocalExample as EndpointExampleScenario };
